import { faTrashCan, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, List, ListItem, ListItemText, MenuItem, Paper, Popper, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { useSnackbar } from "notistack";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FormDefinitions, StagePlan, SubStages } from "../../api/dtos";
import ConfirmRemovePointsDialog from "../../components/dialogs/ConfirmRemovePointsDialog";
import Image from "../../components/layout/Image";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton, StyledIconButton } from "../../components/StyledButton";
import TitleBox from "../../components/TitleBox";
import LotsContext from "../../contexts/data/LotsContext";
import ProjectContext from "../../contexts/data/ProjectContext";
import StageContext from "../../contexts/data/StageContext";
import { DefaultProjectCreateLink, StagePlanEditStageCreateLink } from "../../contexts/layouts/LinkContext";
import usePostLotBulkUpdateCoords from "../../hooks/data/usePostLotUpdateCoordinates";
import usePostStagePlanSave from "../../hooks/data/usePostStagePlanSave";
import useStagePlanByStageIdSubStage from "../../hooks/data/useStagePlanByStageIdSubStage";

type Point = {
    x: number,
    y: number, 
    lotId?: number
}

interface IStagePlanEditProps { 
    imageUrl?: string
}

const StagePlanEdit: FC<IStagePlanEditProps> = ({ imageUrl }) => {
    const theme = useTheme();

    const params = useParams();
    const stageId = Number(params.stageId);

    const {data: stage} = useContext(StageContext);
    const {data: project} = useContext(ProjectContext);

    const lotMetadata = useFormMetadata(FormDefinitions.Lot);

    const stagePlanUpdateUrl = usePostStagePlanSave();
    const lotsUpdateCoordinates = usePostLotBulkUpdateCoords();

    const [selectedSubStage, setSelectedSubStage] = useState<SubStages | null>();
    const {data: lookups} = useLookups(lotMetadata);
    
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    const {data: lots} = useContext(LotsContext);

    const subStages = useMemo(() => {
        return lots?.reduce<SubStages[]>((subs, lot) => 
            lot.subStage && !subs.includes(lot.subStage) 
                ? [...subs, lot.subStage] 
                : subs,[]
    )}, [lots]);

    useEffect(() => {
        setSelectedSubStage(subStages && subStages.length > 0 ? subStages[0] : undefined);
    }, [subStages])

    const {data: stagePlan, refresh: refreshStagePlan} = useStagePlanByStageIdSubStage(stageId, selectedSubStage ?? undefined);
    
    const [currentImageUrl, setCurrentImageUrl] = useState<string>();

    const imageUrlToUse = useMemo(() => currentImageUrl ?? stagePlan?.imageUrl ?? imageUrl ?? "", [currentImageUrl, stagePlan?.imageUrl, imageUrl]);

    const lotsToUse = useMemo(() => {
        return lots?.filter(lot => lot.subStage == selectedSubStage);
    }, [lots, selectedSubStage])
    
    const [points, setPoints] = useState<Point[]>(
        lotsToUse?.filter(lot => lot.plotLeft && lot.plotTop)
            .map(lot => {
                return {
                    x: lot.plotLeft, 
                    y: lot.plotTop, 
                    lotId: lot.id
                } as Point
            }) ?? []
    );

    useEffect(() => setPoints(
        lotsToUse?.filter(lot => lot.plotLeft && lot.plotTop)
            .map(lot => {
                return {
                    x: lot.plotLeft, 
                    y: lot.plotTop, 
                    lotId: lot.id
                } as Point
            }) ?? []
    ), [lotsToUse])

    const [selectedPoint, setSelectedPoint] = useState<Point | null>(null);

    const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);

    const usedLotIds = useMemo(() => {
        return points.map(point => point.lotId)
    }, [lotsToUse, selectedPoint, points]);

    const unusedLotNumbers = useMemo(() => {
        return lotsToUse ? lotsToUse.filter(lot => !usedLotIds.includes(lot.id)).map(lot => lot.number) : [];
    }, [lotsToUse, usedLotIds, points]);

    const snackbar = useSnackbar();

    const [imgDomElement, setImgDomElement] = useState<HTMLImageElement | null>(null);

    const [imgDimensions, setImgDimensions] = useState<{x: number; y: number; } | undefined>();


    useEffect(() => {
        if (!imgDomElement) {
            setImgDimensions(undefined);
            return;
        }

        imgDomElement.onload = () => {
            setImgDimensions({ y: imgDomElement.naturalHeight, x: imgDomElement.naturalWidth });
        }

    }, [imgDomElement, setImgDimensions])

    const handleStagePlanSave = async () => {
        if(stagePlan) {
            const response = await stagePlanUpdateUrl({...stagePlan, imageUrl: currentImageUrl});
            if(!response) {
                snackbar.enqueueSnackbar("Successfully updated Stage Plan!", { variant: "success" });
            }
            else {
                snackbar.enqueueSnackbar("Failed to update Stage Plan!", { variant: "error" });
            }
        }
        else {
            const response = await stagePlanUpdateUrl(new StagePlan({
                imageUrl: currentImageUrl,
                stageId: stageId,
                subStage: selectedSubStage ?? undefined
            }));
            if(!response) {
                snackbar.enqueueSnackbar("Successfully updated Stage Plan!", { variant: "success" });
            }
            else {
                snackbar.enqueueSnackbar("Failed to update Stage Plan!", { variant: "error" });
            }

        }
        await refreshStagePlan();
    }

    const handleSavePoints = async () => {
        const updatedLots = lotsToUse?.map(
            lot => {
                const point = points.filter(point => point.lotId == lot.id)[0];
                return {
                    ...lot,
                    plotLeft: Math.round(point?.x) ?? undefined,
                    plotTop: Math.round(point?.y) ?? undefined
                }
            });

        lotsUpdateCoordinates(stageId, updatedLots ?? []).then(_value => {
            snackbar.enqueueSnackbar("Successfully updated Stage Plan!", { variant: "success" });
        }).catch(() => {
            snackbar.enqueueSnackbar("Failed to update Stage Plan!", { variant: "error" });
        });
    }

    const [xScaleFactor, yScaleFactor] = useMemo(() => {
        const xScaleFactor = (imgDimensions?.x && imgDomElement?.width) 
            ? (imgDimensions.x as number / imgDomElement.width) 
            : 1;
        const yScaleFactor = (imgDimensions?.y && imgDomElement?.height) 
            ? (imgDimensions.y as number / imgDomElement.height) 
            : 1;

        return [xScaleFactor, yScaleFactor];
    }, [imgDimensions?.x, imgDimensions?.y, imgDomElement?.width, imgDomElement?.height])

    window.onresize = () => setImgDimensions({x: imgDimensions?.x ?? 0, y: imgDimensions?.y ?? 0}); // Doing a dodgy but I don't really care
    const handleAddPoint = (event: React.MouseEvent<HTMLImageElement>) => {
        if(anchorEl) {
            setAnchorEl(null);
            return;
        }

        const x = event.pageX - (event.target as any).offsetParent.offsetParent.offsetLeft - plotRadius;
        const y = event.pageY - (event.target as any).offsetParent.offsetParent.offsetTop - plotRadius;

        const naturalX = x * xScaleFactor;
        const naturalY = y * yScaleFactor;

        const newPoint = { 
            x: naturalX,
            y: naturalY,
            lotId: undefined
        };
        setPoints(oldPoints => [...oldPoints, newPoint]);
    }

    const handlePointClick = (event: React.MouseEvent<HTMLDivElement>, point: Point) => {
        if(anchorEl == event.target) {
            setAnchorEl(null);
            setSelectedPoint(null);
        }
        else {
            setSelectedPoint(point);
            setAnchorEl(event.target as HTMLDivElement);
        }
        event.stopPropagation();
    }

    const handleClearUnusedPoints = () => {
        setPoints(points.filter(x => x.lotId != null))
    }

    const handleClearAllPoints = () => {
        setPoints([]);
    }

    const plotRadius = 8;
    return (
        <>
            <SetLinks
                createProjectLink={DefaultProjectCreateLink}
                createStageLink={StagePlanEditStageCreateLink}
                lotLinkEnabled={false}
            />

            <ConfirmRemovePointsDialog 
                open={deleteAllDialogOpen} 
                onConfirm={handleClearAllPoints} 
                onClose={() => setDeleteAllDialogOpen(false)}
            />

            <TitleBox>
                <Typography variant="h2">
                    {project?.name} - {stage?.name} - {
                        selectedSubStage != undefined && 
                        `SubStage ${GetFieldLookupItem(lookups, 'SubStage', selectedSubStage)?.value?.toString()} - `
                    } Stage Plan
                </Typography>
            </TitleBox>
            <Paper
                sx={{
                    padding: theme.spacing(2)
                }}
            >
                <Grid
                    container
                    sx={{
                        display: "flex",
                        justifyContent: "space-between"
                    }}
                    gap={{
                        xs: 2
                    }}
                >
                    <Grid
                        size={{
                            xs: 3,
                            sm: 1.5,
                            md: 1.5,
                            lg: 1.5,
                            xl: 1.5
                        }}

                        minWidth={110}
                    >
                        {
                            subStages &&
                            <TextField
                                label="SubStage"
                                fullWidth
                                variant="outlined"
                                disabled={subStages.length == 0}
                                select
                                value={selectedSubStage ?? ""}
                                onChange={(event) => {
                                    setSelectedSubStage(Number(event.target.value))
                                }}
                            >
                                {
                                    subStages?.map((subStage, index) => 
                                        <MenuItem key={index} value={subStage}><ListItemText primary={GetFieldLookupItem(lookups, 'SubStage', subStage)?.value?.toString()}/></MenuItem>
                                    )
                                }
                            </TextField>
                        }
                    </Grid>
                    <Grid 
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 9,
                            xl: 9
                        }}
                        sx={{
                            display: "flex",
                            alignItems: "center"
                        }}
                    >
                        <TextField 
                            value={imageUrlToUse}
                            fullWidth
                            label="Image URL"
                            variant="outlined"
                            onChange={(event) => setCurrentImageUrl(event.target.value)}
                        />
                    </Grid>
                    <Grid 
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 12,
                            lg: 1,
                            xl: 1
                        }}
                        sx={{
                            display: "flex",
                            justifyContent: {
                                xs: "left",
                                sm: "left",
                                md: "left",
                                lg: "right",
                                xl: "right"
                            },
                            alignItems: "center",
                            paddingRight: theme.spacing(2)
                        }}
                    >
                        <StyledButton
                            onClick={() => handleStagePlanSave()}
                        >
                            Save
                        </StyledButton>
                    </Grid>
                </Grid>
            </Paper>
            <Grid
                container
            >
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 8,
                        xl: 8
                    }}
                    sx={{
                        mt: theme.spacing(2),
                        display: "flex",
                        justifyContent: "space-between"
                    }}
                >
                    <StyledButton
                        onClick={handleClearUnusedPoints}
                    >
                        Clear Unassigned Points
                    </StyledButton>
                    <StyledButton
                        onClick={() => setDeleteAllDialogOpen(true)}
                    >
                        Clear All Points
                    </StyledButton>
                </Grid>
            </Grid>
            <Grid
                sx={{
                    mt: theme.spacing(2)
                }}
                gap={{
                    xs: 6
                }}
                container
            >
                <Grid
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 8,
                        xl: 8
                    }}
                    sx={{
                        maxWidth: imgDimensions?.x,
                        position: "relative",
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%"
                        }}
                        onClick={handleAddPoint}
                    >
                        <Image
                            src={stagePlan?.imageUrl}
                            //ref={imgRef}
                            ref={ref => setImgDomElement(ref)}
                            sx={{
                                position: "relative",
                                display: "block",
                                width: "100%"
                            }}
                            
                        />
                        <Popper
                            open={anchorEl != null}
                            anchorEl={anchorEl}
                            onClick={event => event.stopPropagation()}
                            sx={{
                                width: 100
                            }}
                        >
                            <Paper 
                                sx={{
                                    padding: theme.spacing(1)
                                }}
                            >
                                <Grid
                                    container
                                    gap={theme.spacing(1)}
                                >
                                    <Grid
                                        sx={{
                                            width: "100%"
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            label="Lot" 
                                            size="small"
                                            select
                                            value={selectedPoint?.lotId ?? ""}
                                            onChange={(event) => {
                                                const newPoint = {...selectedPoint, lotId: Number(event.target.value)} as Point;
                                                setPoints(points => points.map(point => point == selectedPoint ? newPoint : point));
                                                setSelectedPoint(_old => newPoint);
                                            }}
                                            slotProps={{
                                                select: {
                                                    MenuProps: {
                                                        sx: {
                                                            maxHeight: 500
                                                        },
                                                    }
                                                }
                                            }}
                                        >
                                            {lotsToUse?.filter(lot => selectedPoint?.lotId == lot.id || !usedLotIds.includes(lot.id)).map(lot => 
                                                <MenuItem value={lot.id}><ListItemText primary={lot.number} /></MenuItem>
                                            )}
                                        </TextField>
                                    </Grid>
                                    <Grid
                                        gap={theme.spacing(2)}
                                        sx={{
                                            display: "flex",
                                            width: "100%",
                                            justifyContent: "center"
                                        }}
                                    >
                                        <Tooltip title="Clear lot">
                                            <StyledIconButton
                                                sx={{
                                                    width: 28
                                                }}
                                                size="small"
                                                onClick={(_event) => {
                                                    const newPoint = {...selectedPoint, lotId: undefined} as Point;
                                                    setPoints(points => points.map(point => point == selectedPoint ? newPoint : point));
                                                    setSelectedPoint(_old => newPoint);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faXmark} />
                                            </StyledIconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete point">
                                            <StyledIconButton
                                                sx={{
                                                    width: 28
                                                }}
                                                size="small"
                                                onClick={(_event) => {
                                                    setPoints(points.filter(x => x != selectedPoint))
                                                    setAnchorEl(null);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTrashCan} />
                                            </StyledIconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Popper>
                        {imgDimensions && points.map((point, i) => {
                            const lot = lotsToUse?.filter(lot => lot.id == point.lotId)[0];
                            const xPercentage = (point.x*100.0/imgDimensions.x);
                            const yPercentage = (point.y*100.0/imgDimensions.y);
                            return (
                            <Tooltip key={i+1}
                                title={
                                    anchorEl 
                                        ? "" 
                                        : lot
                                        ? `Lot ${lot.number}` 
                                        : "No lot"
                                }
                            >
                                <Box
                                    sx={{
                                        fontSize: "0.85rem",
                                        width: plotRadius * 2, 
                                        height: plotRadius * 2,
                                        position: "absolute",
                                        left: `${xPercentage}%`,
                                        top: `${yPercentage}%`,
                                        borderRadius: plotRadius,
                                        color: "white",
                                        backgroundColor: point.lotId == undefined ? "blue" : "green",
                                        backgroundRepeat: "no-repeat",
                                        backgroundSize: `contain`,
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        userSelect: "none",
                                        ":hover" : {
                                            cursor: "pointer"
                                        }
                                    }}
                                    onClick={(event) => handlePointClick(event, point)}
                                    key={i+1}
                                >
                                    {i+1}
                                </Box>
                            </Tooltip>
                        )})}
                    </Box>
                </Grid>
                <Grid
                    spacing={{
                        xs: 1
                    }}
                    size={{
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 3.5,
                        xl: 3.5,                    
                    }}
                >
                    <Box
                        sx={{
                            width: "100%"
                        }}
                    >
                        
                        <Grid 
                            container
                            width="100%"
                            sx={{   
                                flex: '1 1 auto',
                                display:"flex",
                                justifyContent: "space-between",
                                alignItems: 'stretch',
                                maxHeight: 600
                            }}
                            gap={{
                                xs: 2
                            }}
                        >
                            <Grid
                                size={{
                                    xs: 5.5,
                                    sm: 5.5,
                                    md: 5.5,
                                    lg: 5.5,
                                    xl: 5.5
                                }}
                                sx={{
                                    flex: '1 1 auto',
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    maxHeight: 'inherit'
                                }}
                            >
                                <Paper
                                    sx={{
                                        flex: '1 1 auto',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        padding: theme.spacing(2)
                                    }}
                                >
                                    <Typography>    
                                        Assigned Lots:
                                    </Typography>
                                    <List
                                        dense
                                        sx={{
                                            flex: '1 1 auto',
                                            overflow: "auto",
                                            mb: 2
                                        }}
                                    >
                                        {(points && points.filter(x => x.lotId != undefined).length > 0) ? 
                                            points.map((point, index) => {
                                                if(point.lotId == null) {
                                                    return null;
                                                }

                                                const lot = lotsToUse?.filter(lot => lot.id == point.lotId)[0];
                                                return(
                                                    lot
                                                        ? <ListItem key={index+1}>{index+1} - Lot {lot!.number}</ListItem>
                                                        : null
                                                );
                                            })
                                            : "None"
                                        }
                                    </List>
                                    <StyledButton
                                        sx={{
                                            mt: 'auto'
                                        }}
                                        onClick={handleSavePoints}
                                    >
                                        Save
                                    </StyledButton>
                                </Paper>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 5.5,
                                    sm: 5.5,
                                    md: 5.5,
                                    lg: 5.5,
                                    xl: 5.5
                                }}
                                sx={{
                                    flex: '1 1 auto',
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    maxHeight: 'inherit'
                                }}
                            >
                                <Paper
                                    sx={{
                                        flex: '1 1 auto',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        padding: theme.spacing(2)
                                    }}
                                >
                                    <Typography>    
                                        Unassigned lots:
                                    </Typography>
                                    <List
                                        dense
                                        sx={{
                                            flex: '1 1 auto',
                                            overflow: "auto",
                                            paddingRight: theme.spacing(2)
                                        }}
                                    >
                                        {unusedLotNumbers.length > 0 ?
                                            unusedLotNumbers.map((lotNumber, index) => {
                                                return(
                                                    <ListItem
                                                        key={index+1}
                                                    >
                                                        Lot {lotNumber}
                                                    </ListItem> 
                                                );
                                            })
                                            : "None"
                                        }
                                    </List>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        </>
    )
}

export default StagePlanEdit;